<template>
    <div class="flex flex-col gap-5">
        <div v-if="embeddedInNativeForm" class="sr-only absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0">
            <select
                name="plate_country"
                required
                tabindex="-1"
                aria-hidden="true"
                :value="formData.plate_country || ''"
                @change="onNativeCountryChange"
            >
                <option value="" disabled>{{ texts['home-section-hero.blade.php-hero-label-country'] ?? '—' }}</option>
                <option v-for="c in countries" :key="c.code" :value="c.code">
                    {{ c.translated?.name ?? c.name ?? c.code }}
                </option>
            </select>
            <input
                type="date"
                name="start_date"
                required
                tabindex="-1"
                aria-hidden="true"
                :min="minDate"
                :max="maxDate"
                :value="formData.start_date || ''"
                @change="onNativeDateChange"
            />
        </div>
        <div class="col-span-12">
            <label class="block text-xs font-bold text-gray-700 uppercase mb-1">
                {{ texts["home-section-hero.blade.php-category-label"] ?? 'Vehicle class' }}
            </label>
            <div class="vehicle-category-grid grid grid-cols-3 gap-2 p-1 bg-gray-100 rounded-lg min-h-[68px]">
                <button
                    v-for="category in vehicleClassCategories"
                    :key="category.id"
                    type="button"
                    :class="[
                            'vehicle-category-btn flex flex-col items-center justify-center gap-1 py-2 px-2 rounded-md text-xs font-bold transition-all border-2 w-full focus:outline-none',
                            String(formData.category_id ?? '') === String(category.id)
                                ? 'vehicle-category-btn--active bg-green-100 border-green-600 text-brand-blue shadow-sm'
                                : 'border-transparent text-gray-600 hover:bg-white hover:text-brand-blue hover:border-gray-200'
                        ]"
                    @click="selectCategory(category)"
                >
                    <img
                        :src="vehicleCategoryIcon(category.id)"
                        alt=""
                        class="w-[22px] h-[22px] object-contain pointer-events-none"
                    />
                    <span>{{ category?.translated?.title ?? category?.title ?? category?.value ?? '' }}</span>
                </button>
            </div>
        </div>
        <div class="grid grid-cols-12 gap-3">
            <div class="col-span-12">
                <label class="block text-xs font-bold text-gray-700 uppercase mb-1">
                    {{ texts["home-section-hero.blade.php-hero-label-country"] ?? 'Country' }}
                </label>
                <div class="min-h-[56px]">
                    <DropdownField
                        :field="countryField"
                        :step-data="formData"
                        :has-error="false"
                        :angle-down-solid="angleDownSolid"
                        @field-change="handleDropdownFieldChange"
                    />
                </div>
            </div>
            <div class="col-span-12">
                <label class="block text-xs font-bold text-gray-700 uppercase mb-1">
                    {{ texts["home-section-hero.blade.php-hero-label-plate"] ?? 'License plate' }}
                </label>
                <div class="min-h-[60px]">
                    <TextInput
                        :field="licensePlateField"
                        :step-data="formData"
                        :has-error="errors.license_plate"
                        :countries="countries"
                        :image-src="imageSrc"
                        @clear-error="clearError"
                    />
                </div>
            </div>
        </div>
        <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col justify-between">
                <label for="shopping-cart-duration" class="block text-xs font-bold text-gray-700 uppercase mb-1">
                    {{ texts["home-section-hero.blade.php-hero-label-duration"] ?? 'Duration' }}
                </label>
                <select
                    id="shopping-cart-duration"
                    :value="formData.product_id ?? ''"
                    class="block w-full h-11 rounded-lg border border-gray-300 text-gray-900 focus:border-brand-blue focus:ring-brand-blue sm:text-sm py-2.5 px-3"
                    @change="onDurationChange"
                >
                    <option v-for="opt in durationOptions" :key="opt.optionKey" :value="opt.id">
                        {{ opt.label }}
                    </option>
                </select>
            </div>
            <div class="flex flex-col justify-between">
                <label class="block text-xs font-bold text-gray-700 uppercase mb-1">
                    {{ texts['home-section-hero.blade.php-hero-label-valid-from'] ?? 'Valid from' }}
                </label>
                <div ref="datePickerWrapper" class="relative shopping-cart-datepicker-wrap">
                    <button
                        type="button"
                        class="date-input block w-full h-11 pl-3 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-brand-blue focus:border-brand-blue text-gray-900 text-sm text-left"
                        @click="showDatePicker = !showDatePicker"
                    >
                        {{ formattedStartDate || (texts['home-section-hero.blade.php-hero-label-valid-from'] ?? 'Select date') }}
                    </button>
                    <v-date-picker
                        v-if="showDatePicker"
                        :class="[
                            isMobile
                                ? 'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50'
                                : 'absolute right-0 mt-1 z-10'
                        ]"
                        class="custom-datepicker bg-white rounded-xl border shadow-lg"
                        :locale="calendarLocale"
                        :min="minDateObj"
                        :max="maxDateObj"
                        :first-day-of-week="1"
                        :model-value="startDateModelValue"
                        @update:model-value="handleDatePick"
                    />
                </div>
            </div>
        </div>
        <div class="pt-2">
            <button
                type="button"
                @click="handleContinue"
                class="w-full bg-buy-vignette hover:bg-buy-vignette-hover text-white font-black text-lg py-4 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 group"
            >
                {{ texts['home-section-hero.blade.php-hero-button-1'] }}
                <img src="/img/svg/arrow-forward.svg" alt="" class="w-6 h-6 object-contain group-hover:translate-x-1 transition-transform brightness-0 invert" />
            </button>
            <p class="text-center text-xs text-gray-600 mt-3 flex items-center justify-center gap-1">
                <img src="/img/svg/lock.svg" alt="" class="w-4 h-4 object-contain opacity-60" />
                {{ texts['home-section-hero.blade.php-hero-secure-line'] }}
            </p>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getVehicleCategoryIconPath } from '../utils/vehicleCategoryIcons.js';
import {
    getProductDurationDays,
    formatProductDurationLabel,
    compareProductDurations,
} from '../utils/productDuration.js';
import DropdownField from './BuyVignette/components/DropDownField.vue';
import TextInput from './BuyVignette/components/TextInput.vue';
import { VDatePicker } from 'vuetify/components';
import {
    getStartDateRange,
    isStartDateInRange,
    toLocalIsoDate,
} from '../utils/startDateRange.js';

export default {
    name: 'ShoppingCart',
    components: {
        DropdownField,
        TextInput,
        VDatePicker,
    },
    props: {
        angleDownSolid: { type: [String, Object], default: null },
        imageSrc: { type: [String, Object], default: null },
        buyVignetteHref: { type: String, default: '' },
        embeddedInNativeForm: { type: Boolean, default: false },
    },
    data() {
        const { minDate, maxDate } = getStartDateRange();
        return {
            showDatePicker: false,
            minDate,
            maxDate,

            errors: {
                license_plate: false
            }
        };
    },
    computed: {
        calendarLocale() {
            const aliases = {
                cz: 'cs',
                dk: 'da',
                se: 'sv',
                gr: 'el',
                nb: 'no',
                nn: 'no',
            };
            const raw = String(this.lang?.code || '').toLowerCase().trim();
            const normalized = raw.replace('_', '-');
            const base = normalized.split('-')[0] || 'en';
            const mappedBase = aliases[base] || base;
            const candidates = [
                normalized,
                mappedBase,
                `${mappedBase}-${mappedBase.toUpperCase()}`,
            ];
            const supported = Intl.DateTimeFormat.supportedLocalesOf(candidates);
            return supported[0] || 'en';
        },
        ...mapGetters(['texts', 'lang']),
        ...mapGetters('form', ['formData', 'countries', 'productCategories']),
        minDateObj() {
            return new Date(this.minDate + 'T00:00:00');
        },
        maxDateObj() {
            return new Date(this.maxDate + 'T00:00:00');
        },
        countryField() {
            return {
                key: 'plate_country',
                translated: { label: '' },
                options: this.countries || []
            };
        },
        licensePlateField() {
            const placeholder = this.texts['home-section-hero.blade.php-hero-placeholder-plate'] ?? 'e.g. AB 1234';
            return {
                key: 'license_plate',
                type: 'text_input',
                translated: { label: '', placeholder }
            };
        },
        vehicleClassCategories() {
            return this.productCategories || [];
        },
        selectedCategory() {
            return (this.vehicleClassCategories || []).find(
                c => String(c.id) === String(this.formData.category_id)
            );
        },
        durationOptions() {
            if (this.selectedCategory?.products?.length) {
                /**
                 * Build options from whatever durations the category carries
                 * (7 days / 1 month / 6 months / 1 year — combination differs
                 * per vehicle class). Sort by canonical days so the order is
                 * stable across units.
                 */
                return [...this.selectedCategory.products]
                    .sort(compareProductDurations)
                    .map((p) => ({
                        optionKey: `cat-${this.selectedCategory.id}-p-${p.id}`,
                        id: p.id,
                        duration: getProductDurationDays(p),
                        label: this.formatDurationLabel(p),
                        categoryId: this.selectedCategory.id
                    }));
            }
            return [];
        },
        startDateModelValue() {
            const s = this.formData?.start_date;
            if (!s) return null;
            const [y, m, d] = s.split('-').map(Number);
            return new Date(y, m - 1, d);
        },
        formattedStartDate() {
            const s = this.formData?.start_date;
            if (!s) return '';
            const [y, m, d] = s.split('-');
            return `${d}-${m}-${y}`;
        },
        isMobile() {
            return typeof window !== 'undefined' && window.innerWidth < 640;
        },
        buyUrl() {
            const fromProp =
                typeof this.buyVignetteHref === 'string' && this.buyVignetteHref.trim();

            let path = fromProp
                ? this.buyVignetteHref.trim()
                : String(this.$store.getters.defPath || '').trim();

            if (!fromProp && (!path || path === '/')) {
                path = '/buy-vignette';
            }

            return path;
        },
    },
    watch: {
        'lang.code'() {
            this.syncCountryToLanguage(true);
            this.$store.dispatch('form/loadProductCatalog').catch(() => {});
        },
        formData: {
            handler() {
                this.persistCartToStore();
            },
            deep: true
        },
        'formData.plate_country'() {
            this.persistCartToStore();
        },
        'formData.license_plate'() {
            this.persistCartToStore();
        },
        /**
         * Auto-select the first available category whenever the catalog
         * loads (or reloads after a language switch) and no category is
         * currently chosen. This must not override a category the user has
         * already picked or that was restored from localStorage.
         */
        vehicleClassCategories: {
            handler(list) {
                if (!Array.isArray(list) || list.length === 0) return;

                const currentId = this.formData?.category_id;
                const currentExists = currentId != null
                    && list.some((c) => String(c.id) === String(currentId));

                if (!currentExists) {
                    this.selectCategory(list[0]);
                }
            },
            immediate: true
        },
        durationOptions: {
            handler(opts) {
                if (!opts?.length) return;

                const currentId = this.formData?.product_id;
                const hasId = currentId != null && currentId !== '';
                const isValid = hasId && opts.some((o) => String(o.id) === String(currentId));

                /**
                 * Always keep a duration selected. `durationOptions` is
                 * sorted ascending by canonical days (productCatalog.js +
                 * compareProductDurations), so opts[0] is the shortest
                 * vignette in the current category — "7 days" for every
                 * category in the current catalog.
                 *
                 * Picks the first option in two cases:
                 *   - no product_id stored at all (fresh visit, cleared
                 *     localStorage, language switch),
                 *   - stored product_id no longer exists in the category
                 *     (e.g. catalog reload after locale change).
                 */
                if (!hasId || !isValid) {
                    const firstOpt = opts[0];
                    this.$store.commit('form/SET_FORM_DATA', {
                        product_id: firstOpt?.id ?? null,
                        selected_duration: firstOpt?.duration ?? null
                    });
                }

                this.persistCartToStore();
            },
            immediate: true
        }
    },
    created() {
        this.$store.dispatch('form/loadSavedData');
    },
    mounted() {
        this.$store.dispatch('form/loadInitialData').then(() => {
            if (!this.$store.state.form.productCategories?.length) {
                return this.$store.dispatch('form/loadProductCatalog');
            }
            return null;
        }).then(() => {
            this.$nextTick(this.initFormDataDefaults);
        }).catch(() => {});
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        vehicleCategoryIcon(categoryId) {
            return getVehicleCategoryIconPath(categoryId);
        },
        selectCategory(category) {
            const firstProduct = category?.products?.[0];

            this.$store.commit('form/SET_FORM_DATA', {
                category_id: category.id,
                product_id: firstProduct?.id ?? null,
                /** Canonical days so downstream code never has to re-interpret duration_type. */
                selected_duration: firstProduct ? getProductDurationDays(firstProduct) : null
            });

            this.persistCartToStore();
        },
        clearError(field) {
            if (this.errors[field] !== undefined) {
                this.errors[field] = false;
            }
        },
        async handleContinue() {
            let hasError = false;

            if (!this.formData?.license_plate?.trim()) {
                this.errors.license_plate = true;
                hasError = true;
            }

            if (hasError) return;

            try {
                await this.$store.dispatch('form/validateStep', 'step1');
            } catch (e) {
                return;
            }

            window.location.href = this.buyUrl;
        },
        extractDuration(product) {
            /**
             * Canonical days for legacy callers (e.g. selected_duration in
             * formData). Falls back to URL-slug parsing only if both the API
             * count and duration_type are missing.
             */
            const fromApi = getProductDurationDays(product);
            if (fromApi > 0) return fromApi;
            const match = product.path?.match(/(\d+)-day/);
            return match ? parseInt(match[1], 10) : 0;
        },
        formatDurationLabel(product) {
            return (
                formatProductDurationLabel(product)
                || product?.translated?.title
                || product?.title
                || product?.name
                || ''
            );
        },
        initFormDataDefaults() {
            const fd = this.formData;
            const { minDate, maxDate } = getStartDateRange();
            this.minDate = minDate;
            this.maxDate = maxDate;
            const updates = {};

            if (!fd.start_date || fd.start_date < minDate || fd.start_date > maxDate) {
                updates.start_date = minDate;
            }

            if (Object.keys(updates).length) {
                this.$store.commit('form/SET_FORM_DATA', updates);
                this.persistCartToStore();
            }

            const currentLangCode = String(this.lang?.code || '').toLowerCase().trim();
            let appliedLangCode = '';
            try {
                appliedLangCode = String(localStorage.getItem('cartLangCodeApplied') || '').toLowerCase().trim();
            } catch (e) {}

            const forceLanguageSync = !!currentLangCode && currentLangCode !== appliedLangCode;
            this.syncCountryToLanguage(forceLanguageSync);
        },
        syncCountryToLanguage(force = false) {
            return this.$store.dispatch('form/syncVehicleCountryToLanguage', { force });
        },
        /**
         * Fires only on user-driven dropdown clicks (DropdownField.selectOption sets manual=true).
         * Clears the autosync mirror so syncVehicleCountryToLanguage will bail out for
         * this plate_country on the next run, even when the UI language changes.
         */
        handleDropdownFieldChange(payload) {
            if (payload?.key !== 'plate_country' || !payload.manual) return;
            this.$store.dispatch('form/markPlateCountryManual');
        },
        persistCartToStore() {
            this.$store.dispatch('form/saveDataToLocalStorage');
        },
        /**
         * No "empty" option in the duration select anymore — there's always
         * a valid product selected. Just sync the new product_id and its
         * canonical days into formData.
         */
        onDurationChange(e) {
            const productId = Number(e.target.value) || null;
            if (!productId) return;
            const product = (this.selectedCategory?.products || []).find(
                (p) => Number(p.id) === productId
            );
            const duration = product ? this.extractDuration(product) : null;
            this.$store.commit('form/SET_FORM_DATA', {
                product_id: productId,
                selected_duration: duration,
                category_id: this.selectedCategory?.id ?? null
            });
            this.persistCartToStore();
        },
        handleDatePick(date) {
            let isoDate = '';
            if (typeof date === 'string') isoDate = date;
            else if (date instanceof Date) {
                isoDate = toLocalIsoDate(date);
            } else return;

            if (!isStartDateInRange(isoDate)) return;

            this.$store.commit('form/SET_FORM_DATA', { start_date: isoDate });
            this.persistCartToStore();
            this.showDatePicker = false;
        },
        handleClickOutside(event) {
            if (this.$refs.datePickerWrapper && !this.$refs.datePickerWrapper.contains(event.target)) {
                this.showDatePicker = false;
            }
        },
    }
};
</script>

<style scoped>
.vehicle-category-btn--active {
    background-color: #dcfce7 !important;
    border-color: #16a34a !important;
    box-shadow: 0 0 0 2px #16a34a;
}
</style>
